import { create } from "zustand";
import { persist } from "zustand/middleware";
import { defaultConfiguration, layouts } from "./data";
import type { HouseConfiguration, StepId, ViewerMode } from "./types";

interface ConfiguratorState {
  configuration: HouseConfiguration;
  step: StepId;
  viewerMode: ViewerMode;
  history: HouseConfiguration[];
  setStep: (step: StepId) => void;
  setViewerMode: (mode: ViewerMode) => void;
  update: (patch: Partial<HouseConfiguration>) => void;
  setLayout: (layoutId: string) => void;
  toggleOption: (id: string) => void;
  undo: () => void;
  reset: () => void;
  load: (configuration: HouseConfiguration) => void;
}

const clone = (configuration: HouseConfiguration) => structuredClone(configuration);

export const useConfiguratorStore = create<ConfiguratorState>()(
  persist(
    (set) => ({
      configuration: clone(defaultConfiguration),
      step: "model",
      viewerMode: "3d",
      history: [],
      setStep: (step) => set({ step }),
      setViewerMode: (viewerMode) => set({ viewerMode }),
      update: (patch) =>
        set((state) => ({
          history: [...state.history.slice(-19), clone(state.configuration)],
          configuration: { ...state.configuration, ...patch }
        })),
      setLayout: (layoutId) =>
        set((state) => {
          const layout = layouts.find((item) => item.id === layoutId);
          if (!layout) return state;
          return {
            history: [...state.history.slice(-19), clone(state.configuration)],
            configuration: {
              ...state.configuration,
              layoutId,
              bedrooms: layout.bedrooms,
              bathrooms: layout.bathrooms,
              office: layoutId === "layout-c"
            }
          };
        }),
      toggleOption: (id) =>
        set((state) => {
          const selected = state.configuration.options.includes(id);
          return {
            history: [...state.history.slice(-19), clone(state.configuration)],
            configuration: {
              ...state.configuration,
              options: selected
                ? state.configuration.options.filter((optionId) => optionId !== id)
                : [...state.configuration.options, id]
            }
          };
        }),
      undo: () =>
        set((state) => {
          const previous = state.history.at(-1);
          if (!previous) return state;
          return { configuration: previous, history: state.history.slice(0, -1) };
        }),
      reset: () => set({ configuration: clone(defaultConfiguration), history: [], step: "model" }),
      load: (configuration) => set({ configuration: clone(configuration), history: [] })
    }),
    {
      name: "cbciso-house-configuration",
      partialize: (state) => ({ configuration: state.configuration })
    }
  )
);
